import classnames from 'classnames';
import {
  NormalButton,
  IcArrowLeft as icArrowLeft,
  IcArrowRight as icArrowRight,
} from '@/shared/ui';
import { TYPE_RHYTHM_EVENT_ENUM } from '@/shared/config';
import { convertToMomentJs } from '@/shared/lib/aiUtils';
import { isValidHr } from '@/entities/heart-rate';
import useDailySummaryEventHrInfo from './useDailySummaryEventHrInfo';

export type DailySummaryEventHrInfoProps = {
  eventIndexes?: any[];
  events?: any[];
  isActive?: boolean;
  aiChannel?: any;
  setSelectedEventIndex?: (index: any) => void;
  defaultIndex?: number;
  timezoneOffset?: number;
};

/**
 * Renders the info panel for the event or heart-rate point selected on the daily summary chart. Styles itself by artifact, AFib or generic event type, shows prev and next navigation when several events share the point, formats the start or point timestamp in the report timezone, prints min, avg and max HR with NA for invalid values, and offers the Set as Max/Min HR button only for a plain point with a valid average.
 *
 * @param props - Panel inputs including the timezone offset used to format the displayed timestamps.
 * @returns The event info panel with its timestamps, heart-rate figures and optional actions.
 */
const DailySummaryEventHrInfo = ({
  eventIndexes,
  events: eventsProp,
  isActive: isActiveProp,
  aiChannel,
  setSelectedEventIndex,
  defaultIndex,
  timezoneOffset,
}: DailySummaryEventHrInfoProps) => {
  const {
    eventIndex,
    events,
    isActive,
    event,
    eventType,
    channel,
    onClickLeft,
    onClickRight,
    onClickSetMaxMinHr,
  } = useDailySummaryEventHrInfo({
    eventIndexes,
    events: eventsProp,
    isActive: isActiveProp,
    aiChannel,
    setSelectedEventIndex,
    defaultIndex,
  });

  return (
    <div
      className={classnames(
        'event-info',
        event.type
          ? event.type === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT
            ? '--artifact-event-clicked'
            : event.type === TYPE_RHYTHM_EVENT_ENUM.AFIB
              ? '--afib-event-clicked'
              : '--event-clicked'
          : '',
        isActive ? '--active' : '',
      )}
    >
      <div className="event-info-content">
        <div className="left-container">
          <div className="event-info-item">
            {events?.length > 1 && (
              <div className="circle-navigation">
                <button
                  id="daily-summary-event-nav-prev-btn"
                  type="button"
                  style={{ border: 'none', background: 'transparent', padding: 0 }}
                  onClick={onClickLeft}
                >
                  <img src={icArrowLeft} alt="icon arrow" />
                </button>
                <div className="text-event-index">{`#${eventIndex + 1}`}</div>
                <button
                  id="daily-summary-event-nav-next-btn"
                  type="button"
                  style={{ border: 'none', background: 'transparent', padding: 0 }}
                  onClick={onClickRight}
                >
                  <img src={icArrowRight} alt="icon arrow" />
                </button>
              </div>
            )}
            {eventType ? (
              <div className="info-item">
                <div className="title">Start time</div>
                <div className="time">
                  {`${convertToMomentJs(event.start, timezoneOffset).format('MM/DD/YYYY, HH:mm:ss')}`}
                </div>
              </div>
            ) : (
              <div className="info-item">
                <div className="title">Time</div>
                <div className="time">
                  {`${convertToMomentJs(event.startTimeEpoch, timezoneOffset).format('MM/DD/YYYY, HH:mm:ss')}`}
                </div>
              </div>
            )}
            {channel && (
              <div className="info-item">
                <div className="title">Channel</div>
                <div className="time">{`CH${channel}`}</div>
              </div>
            )}
            {eventType === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT ? null : (
              <div className="info-item --multi">
                <div>
                  <div className="title">Min HR</div>
                  <strong>{isValidHr(event.minHr) ? `${event.minHr} bpm` : 'NA'}</strong>
                </div>
                <div>
                  <div className="title">Avg HR</div>
                  <strong>{isValidHr(event.avgHr) ? `${event.avgHr} bpm` : 'NA'}</strong>
                </div>
                <div>
                  <div className="title">Max HR</div>
                  <strong>{isValidHr(event.maxHr) ? `${event.maxHr} bpm` : 'NA'}</strong>
                </div>
              </div>
            )}
            {eventType && (
              <div className="info-item">
                <div className="title">Duration</div>
                <strong>{event.duration}</strong>
              </div>
            )}
          </div>
        </div>
        {eventType
          ? null
          : isValidHr(event.avgHr) && (
              <div className="right-container">
                <NormalButton
                  id="set-min-max-hr-btn"
                  className="set-min-max-hr-btn"
                  buttonName="Set as Max/Min HR"
                  onClick={onClickSetMaxMinHr}
                />
              </div>
            )}
      </div>
    </div>
  );
};

export default DailySummaryEventHrInfo;
